import { useGetAppByIdQuery } from "@store/apis/magicoApi";
import { setAppConfig } from "@store/slices/appConfig";
import { applyFonts, applyThemeColors } from "@utilities/appUtils";
import { appConfig } from "app.config";
import { useEffect, useState } from "react";

import { useAppDispatch } from "./useRedux";
import { useRemoteTranslations } from "./useRemoteTranslations";


export const useAppConfigFetcher = (appId?: string) => {
  const numericAppId = appId ? Number.parseInt(appId, 10) : 0;
  // NaN would serialise as `{"id":null}` — skip instead of sending it.
  const hasValidAppId = Number.isFinite(numericAppId) && numericAppId > 0;

  const { data, isLoading, isSuccess, error } = useGetAppByIdQuery(numericAppId, {
    skip: !hasValidAppId,
  });
  const dispatch = useAppDispatch();
  const { loadTranslations } = useRemoteTranslations();
  // Which app the config was applied for — a plain boolean would stay latched across an
  // appId change and render children against the previous tenant.
  const [appliedFor, setAppliedFor] = useState<number | null>(null);

  useEffect(() => {
    if (!data) return;

    dispatch(setAppConfig(data));
    if (data.colors) {
      applyThemeColors(data.colors);
    }

    if (data.configs) {
      applyFonts(data.configs);
    }

    loadTranslations(numericAppId);
    setAppliedFor(numericAppId);
  }, [data, dispatch, loadTranslations, numericAppId]);

  // `isLoading` turns false one commit before the effect stores `configs`, so gating on it
  // flashes a frame of empty config. Last term covers an empty 2xx body (`data === null`,
  // no error, effect never runs) — without it the spinner would hang forever.
  const isReady =
    !hasValidAppId || !!error || appliedFor === numericAppId || (isSuccess && !data);

  return { isLoading, error, isReady, appConfig };
};
